<section class="mod-page res-page" data-module="resolution">
  <header class="page-header">
    <div>
      <p class="eyebrow">Resolution dashboard</p>
      <h1>DM decisions</h1>
    </div>
    <div class="res-header-meta">
      <div class="res-stat">
        <span>In queue</span>
        <strong>{{ pending().length }}</strong>
      </div>
      <div class="res-stat">
        <span>Resolved</span>
        <strong>{{ closed().length }}</strong>
      </div>
    </div>
  </header>

  @if (message()) {
    <p class="toast">{{ message() }}</p>
  }

  <div class="res-layout">
    <section class="res-queue panel">
      <div class="res-section-head">
        <h2>Case queue</h2>
        <p>Click a case to decide</p>
      </div>

      <ul class="res-queue-list">
        @for (item of pending(); track item.id) {
          <li>
            <button
              type="button"
              class="res-case"
              [class.is-active]="selected()?.id === item.id"
              (click)="selectedId.set(item.id)"
            >
              <div class="res-case-top">
                <strong class="res-case-id">{{ item.caseNo }}</strong>
                <span [class]="'res-pill ' + statusTone(item.status)">{{ statusLabel(item.status) }}</span>
              </div>
              <p class="res-case-subject">{{ item.subject }}</p>
              <p class="res-case-meta">Assigned role · {{ item.assignedRole || item.officer }}</p>
            </button>
          </li>
        } @empty {
          <li class="res-empty">No cases awaiting DM decision.</li>
        }
      </ul>

      @if (closed().length) {
        <div class="res-closed">
          <h3>Recently resolved</h3>
          <ul>
            @for (r of closed(); track r.id) {
              <li>
                <strong>{{ r.caseNo }}</strong>
                <span>{{ r.outcome }}</span>
              </li>
            }
          </ul>
        </div>
      }
    </section>

    @if (selected(); as item) {
      <section class="res-decision panel">
        <div class="res-section-head">
          <h2>Decision desk</h2>
          <p>Act on the selected case</p>
        </div>

        <div class="res-case-banner">
          <div>
            <p class="res-banner-id">{{ item.caseNo }}</p>
            <h3>{{ item.subject }}</h3>
          </div>
          <span [class]="'res-pill ' + statusTone(item.status)">{{ statusLabel(item.status) }}</span>
        </div>

        <dl class="res-facts">
          <div class="fact-id">
            <dt>Tracking</dt>
            <dd>{{ item.trackingId }}</dd>
          </div>
          <div class="fact-dept">
            <dt>Department</dt>
            <dd>{{ item.department || '—' }}</dd>
          </div>
          <div class="fact-officer">
            <dt>Assigned</dt>
            <dd>{{ item.officer || item.assignedRole || '—' }}</dd>
          </div>
          <div class="fact-status">
            <dt>Status</dt>
            <dd>{{ statusLabel(item.status) }}</dd>
          </div>
          @if (item.openedAt) {
            <div class="fact-date">
              <dt>Opened</dt>
              <dd>{{ item.openedAt | date: 'd MMM y' }}</dd>
            </div>
          }
          @if (item.hearingDate) {
            <div class="fact-hearing">
              <dt>Hearing</dt>
              <dd>{{ formatHearing(item.hearingDate, item.hearingTime) }}</dd>
            </div>
          }
        </dl>

        @if (item.description) {
          <div class="res-case-detail">
            <h3>Case details</h3>
            <p>{{ item.description }}</p>
          </div>
        }

        <div class="res-next">
          <span class="res-next-label">Next step</span>
          <p>{{ nextStep() }}</p>
        </div>

        @if (item.status === 'scheduled' || item.status === 'awaiting_hearing') {
          <div class="res-hm-link">
            <p>
              @if (item.status === 'awaiting_hearing') {
                Waiting for Peshkar to book a slot.
              } @else {
                Hearing is scheduled — view date, time, venue, and officer on Hearing Management.
              }
            </p>
            <a routerLink="/hearing-management" class="btn primary">Open Hearing Management</a>
          </div>
        }

        @if (item.notes) {
          <div class="res-officer-report">
            <div class="res-officer-report-head">
              <h3>Officer investigation report</h3>
              <p>Submitted by desk · {{ item.assignedRole || item.officer }}</p>
            </div>
            <div class="res-officer-report-body">{{ item.notes }}</div>
          </div>
        } @else if (item.status === 'investigation_completed') {
          <div class="res-officer-report is-empty">
            <p>Report marked complete, but no investigation text was attached.</p>
          </div>
        }

        <div class="res-attachments">
          <div class="res-attachments-head">
            <h3>Case attachments</h3>
            <span>{{ attachments().length }} file{{ attachments().length === 1 ? '' : 's' }}</span>
          </div>
          <ul class="res-files">
            @for (file of attachments(); track file.id) {
              <li>
                <span [class]="'file-icon ' + fileKindClass(file.kind)">
                  {{ file.kind === 'PDF' ? 'PDF' : file.kind === 'DOC' ? 'DOC' : 'IMG' }}
                </span>
                <div class="file-meta">
                  <span class="file-name">{{ file.fileName }}</span>
                  @if (formatSize(file.sizeBytes); as size) {
                    <span class="file-size">{{ size }}</span>
                  }
                </div>
                <button type="button" class="res-file-open" (click)="openAttachment(file)">
                  Open
                </button>
              </li>
            } @empty {
              <li class="res-files-empty">No PDFs or images uploaded for this case yet.</li>
            }
          </ul>
        </div>

        <label class="res-notes">
          Decision / hearing notes
          <textarea
            class="field"
            rows="5"
            [ngModel]="notes()"
            (ngModelChange)="notes.set($event)"
            placeholder="Order text, hearing reason, or observations…"
          ></textarea>
        </label>

        <div class="res-actions">
          @if (canDecide()) {
            @if (canNeedHearing(item)) {
              <button type="button" class="btn primary" [disabled]="busy()" (click)="needHearing()">
                {{ busy() ? 'Sending…' : needHearingLabel(item) }}
              </button>
            } @else if (item.status === 'routed' || item.status === 'under_review') {
              <p class="muted">Need Hearing unlocks after the assigned officer submits their investigation report.</p>
            }
            @if (item.status === 'scheduled') {
              <button type="button" class="btn primary" [disabled]="busy()" (click)="markHeard()">
                {{ busy() ? 'Saving…' : 'Hearing Conducted' }}
              </button>
            }
            @if (canResolve(item.status)) {
              <button type="button" class="btn primary" [disabled]="busy()" (click)="issueOrder(false)">
                Issue Final Resolution
              </button>
              <button type="button" class="btn" [disabled]="busy()" (click)="issueOrder(true)">
                Resolve &amp; Close
              </button>
            }
          } @else {
            <p class="muted">View only — DM issues resolutions.</p>
          }
          <button type="button" class="btn ghost" (click)="download()">Download Order</button>
        </div>
      </section>
    } @else {
      <section class="res-decision panel res-decision-empty">
        <p class="muted">Select a case from the queue to review and decide.</p>
      </section>
    }
  </div>
</section>
